import React, { FC, memo, useCallback, useEffect } from 'react'
import { Controller, Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSelector } from 'react-redux'

import { NfcTag, useNfcTagCreateMutation, useNfcTagUpdateMutation } from '../../../common/redux/api/web-api'
import convertHexToBigInt, { reverseHex } from '../../../lib/convertHexToBigInt'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { isFetchBaseQueryError } from '../../../common/redux/api/fetchBaseQueryWithReauth'
import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import convertBigIntToHex from '../../../lib/convertBigIntToHex'
import Input from '../../../common/components/Input'
import { pad } from '../../../common/services/utilsService'
import { MAX_COMMENT_LENGTH, MAX_EXTERNAL_ID_LENGTH } from '../../../common/constants/InputsLength'
import { Combobox } from '../../../common/components/combobox'
import { useCompanyQuery } from '../../hooks/useFetchEndpoints'
import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import useText from '../../hooks/useText'
import { RootState } from '../../../partner/redux/store'

type RfidTagModalProps = { onClose: () => void; rfidTag?: NfcTag; isNew?: boolean }

type FormDataType = {
    isReversed: boolean
    hexId?: string | null
    decId?: string | null
    comment?: string | null
    externalId?: string | null
    companyId?: string | null
}

export const RfidTagModal: FC<RfidTagModalProps> = memo(({ onClose, rfidTag, isNew = false }) => {
    const text = useText()
    const { partnerId, selectedCompanyId } = useSelector((state: RootState) => state.user)
    const { data: organisations = [] } = useCompanyQuery(partnerId)
    const [createRfidTag] = useNfcTagCreateMutation()
    const [updateRfidTag] = useNfcTagUpdateMutation()

    const isAddingNewTag = isNew || !rfidTag || !rfidTag.id

    const schema = yup.object().shape({
        hexId: yup
            .string()
            .nullable()
            .max(20, text('msgHexToLong'))
            .matches(/^[0-9A-Fa-f]+$/g, text('msgInvalidHexadecimal')),
        decId: yup
            .number()
            .integer()
            .transform((value, originalValue) => {
                return originalValue === '' ? undefined : value
            })
            .moreThan(0, text('msgInvalidDecimal'))
            .typeError(text('msgInvalidDecimal'))
            .required(text('msgDecimalRequired')),
        externalId: yup
            .string()
            .nullable()
            .max(MAX_EXTERNAL_ID_LENGTH, text('msgLengthLimit', { maxLength: MAX_EXTERNAL_ID_LENGTH })),
        comment: yup
            .string()
            .nullable()
            .max(MAX_COMMENT_LENGTH, text('msgLengthLimit', { maxLength: MAX_COMMENT_LENGTH })),
    })

    const {
        handleSubmit,
        register,
        setValue,
        watch,
        control,
        formState: { errors, isSubmitting, isValid },
    } = useForm<FormDataType>({
        mode: 'all',
        resolver: yupResolver(schema) as unknown as Resolver<FormDataType>,
        defaultValues: {
            ...rfidTag,
            isReversed: false,
            hexId: rfidTag?.id || '',
            decId: rfidTag?.id ? convertHexToBigInt(rfidTag?.id) : undefined,
            comment: rfidTag?.comment || '',
            externalId: rfidTag?.externalId || '',
            companyId: isAdminDomain ? selectedCompanyId : rfidTag?.companyId || '',
        },
    })

    const isReversed = watch('isReversed')
    const hexId = watch('hexId')

    const hexNumberChanged = useCallback(
        (id: string) => {
            const hexRegex = /^[0-9A-Fa-f]+$/g
            if (hexRegex.test(id)) {
                setValue('decId', convertHexToBigInt(isReversed && id.length <= 20 ? reverseHex(pad(id, true)) : id), {
                    shouldValidate: true,
                })
            } else {
                setValue('decId', undefined)
            }
            setValue('hexId', id)
        },
        [isReversed, setValue]
    )

    const decimalNumberChanged = (dec: string) => {
        setValue('decId', dec, { shouldValidate: true })
        const decRegex = /^[0-9]+$/g
        if (dec && decRegex.test(dec)) {
            let newHex = convertBigIntToHex(dec)
            if (newHex.length <= 8) {
                const toAdd = 8 - newHex.length
                newHex = '0'.repeat(toAdd) + newHex
            } else if (newHex.length <= 14) {
                const toAdd = 14 - newHex.length
                newHex = '0'.repeat(toAdd) + newHex
            } else if (newHex.length <= 20) {
                const toAdd = 20 - newHex.length
                newHex = '0'.repeat(toAdd) + newHex
            }
            setValue('hexId', isReversed && newHex.length <= 20 ? reverseHex(newHex) : newHex, { shouldValidate: true })
        } else {
            setValue('hexId', undefined)
        }
    }

    useEffect(() => {
        if (hexId && hexId?.length > 0) hexNumberChanged(hexId)
    }, [hexId, hexNumberChanged])

    const onSave = (data: FormDataType) => {
        if (isAddingNewTag) {
            createRfidTag({
                nfcTag: {
                    id: data.hexId?.toUpperCase(),
                    externalId: data.externalId,
                    comment: data.comment,
                    partnerId: partnerId,
                    companyId: data.companyId,
                },
            })
                .unwrap()
                .then(() => {
                    onClose()
                    notifySuccess(text('msgRFIDCreated'))
                })
                .catch((error) => {
                    if (isFetchBaseQueryError(error)) {
                        if (error.status === 422) {
                            notifyError(text('msgRFIDNotAccepted'))
                        } else {
                            notifyError(text('msgRFIDNotCreated'))
                        }
                    }
                })
        } else {
            updateRfidTag({
                nfcTag: data,
            })
                .unwrap()
                .then(() => {
                    onClose()
                    notifySuccess(text('msgRFIDUpdated'))
                })
                .catch((error) => {
                    if (isFetchBaseQueryError(error)) {
                        if (error.status === 422) {
                            notifyError(text('msgRFIDNotAccepted'))
                        } else {
                            notifyError(text('msgRFIDNotUpdated'))
                        }
                    }
                })
        }
    }

    return (
        <BootstrapModalLayout
            title={text(isAddingNewTag ? 'lblNewRFIDTagModal' : 'lblRFIDTagModal')}
            actions={[
                {
                    label: text('btnCancel'),
                    btn: 'btn-secondary',
                    callback: onClose,
                },
                {
                    label: text('btnSave'),
                    btn: 'btn-ok',
                    callback: handleSubmit(onSave),
                    disabled: !isValid,
                    loading: isSubmitting,
                },
            ]}
        >
            <form id="new-rfid-tag-form">
                {isAddingNewTag && (
                    <div className="inline-flex w-full align-items-center">
                        <Input
                            wrapperClassName="w-min pr-3"
                            id="reverse-id-tag-checkbox"
                            type="checkbox"
                            disabled={!!rfidTag?.id}
                            {...register('isReversed')}
                        />
                        {text('lblReverseIdField')}
                    </div>
                )}

                <Input
                    id="tag-hex-id"
                    wrapperClassName="mt-6"
                    labelText={text('lblHexIdField')}
                    error={errors.hexId?.message}
                    disabled={!!rfidTag?.id}
                    required
                    {...register('hexId', {
                        onChange: (e) => hexNumberChanged(e.target.value),
                    })}
                />
                <Input
                    id="tag-dec-id"
                    wrapperClassName="mt-6"
                    labelText={text('lblDecIdField')}
                    error={errors.decId?.message}
                    disabled={!!rfidTag?.id}
                    required
                    {...register('decId', {
                        onChange: (e) => decimalNumberChanged(e.target.value),
                    })}
                />
                <Input
                    id="tag-external-id"
                    wrapperClassName="mt-6"
                    labelText={text('lblExternalId')}
                    error={errors.externalId?.message}
                    {...register('externalId')}
                />
                <Input
                    id="comment"
                    wrapperClassName="mt-6"
                    labelText={text('lblComment')}
                    error={errors.comment?.message}
                    {...register('comment')}
                />
                {(isSupportDomain && !isAddingNewTag) || isPartnerDomain ? (
                    <Controller
                        name="companyId"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            return (
                                <Combobox
                                    id="organisation"
                                    data-testid="organisation-combobox"
                                    value={value}
                                    onChange={(option) => {
                                        onChange(option.value)
                                    }}
                                    labelText={text('lblOrganisationField')}
                                    options={organisations?.map(({ id, name }) => ({
                                        value: id || '',
                                        label: name || '',
                                    }))}
                                    error={errors.companyId?.message}
                                    wrapperClassName="mt-6"
                                />
                            )
                        }}
                    />
                ) : null}
            </form>
        </BootstrapModalLayout>
    )
})
